import pdfMake from 'pdfmake/build/pdfmake'
import { fmtThaiDateRange, formatYmd } from '.'
import vfs from './vfs'

export type ExportAgenda = {
    agendaId: number
    agendaNo: string
    agendaName?: string
    meetingDate: string
    meetingStartTime?: string
    meetingEndTime?: string
    meetingLocation?: string
    topics?: TopicNode[]
}

export type TopicNode = {
    topicId?: number | string
    topicName?: string
    topicDescription?: string | null
    children?: TopicNode[]
    projects?: Array<{
        projectId: number | string
        projectNumber?: string | null
        projectDescription: string
    }>
}

type Align = 'left' | 'center' | 'right' | 'justify'

const INDENT_STEP_TWIP = 720
const stripOutlinePrefix = (line: string) => line.replace(/^\d+(?:\.\d+)+\s+/, '').trim()

const ensurePdfMakeThaiFonts = () => {
    ;(pdfMake as any).vfs = vfs as any
    pdfMake.fonts = { Sarabun: { normal: 'Sarabun-Regular.ttf', bold: 'Sarabun-Bold.ttf' } }
}

const twipToPt = (twip: number) => twip / 20
const hpToPt = (hp: number) => hp / 2

const P = (text: string, align?: Align, indentLeftTwip = 0, sizeHp = 20) => ({
    text,
    alignment: align,
    fontSize: hpToPt(sizeHp),
    margin: [twipToPt(indentLeftTwip), 0, 0, 6],
})

const PBold = (text: string, align?: Align, sizeHp = 20) => ({
    text,
    alignment: align,
    bold: true,
    fontSize: hpToPt(sizeHp),
    margin: [0, 0, 0, 6],
})

const buildFirstLinePadSafe = (firstLineTwip = 720, sizeHp = 20, wordSpaceEm = 0.25, useNbsp = true) => {
    const fontSizePt = hpToPt(sizeHp)
    const indentPt = twipToPt(firstLineTwip)
    const indentEm = indentPt / fontSizePt
    const count = Math.max(1, Math.round(indentEm / wordSpaceEm))
    const sp = useNbsp ? '\u00A0' : ' '
    return sp.repeat(count)
}

const PFirstLinePdf = (text: string, align?: Align, firstLineTwip = 720, sizeHp = 20) => ({
    text: buildFirstLinePadSafe(firstLineTwip, sizeHp) + (text ?? ''),
    alignment: align,
    fontSize: hpToPt(sizeHp),
    preserveLeadingSpaces: true,
    margin: [0, 0, 0, twipToPt(120)],
})

const renderProjects = (
    projects: TopicNode['projects'],
    prefix: string,
    acc: any[],
    baseIndentTwip = INDENT_STEP_TWIP,
) => {
    if (!projects || projects.length === 0) return

    projects.forEach((proj) => {
        const no = proj.projectNumber || proj.projectId
        if (no != null && String(no).trim() !== '') {
            acc.push(P(`โครงการวิจัยเลขที่ ${no}`, 'left', 720))
        }
        const rawDesc = proj.projectDescription ?? ''
        rawDesc
            .split(/\r?\n/)
            .map((line) => stripOutlinePrefix(line))
            .filter((line) => line !== '')
            .forEach((line) => {
                acc.push(P(line, 'left', baseIndentTwip))
            })
        acc.push(P('', 'left', 0))
    })
}

const renderTopics = (topics: TopicNode[] | undefined, acc: any[], prefix: string[] = [], depth = 0) => {
    if (!topics || topics.length === 0) return
    topics.forEach((topic, i) => {
        const idx = (i + 1).toString()
        const nextPrefix = [...prefix, idx]
        const numbering = nextPrefix.join('.')
        const titleIndent = INDENT_STEP_TWIP * depth
        const descIndent = INDENT_STEP_TWIP * (depth + 1)
        const projIndent = INDENT_STEP_TWIP * (depth + 1)
        if (depth === 0) acc.push(P(`วาระที่ ${idx} ${topic.topicName ?? ''}`, 'left', titleIndent))
        else acc.push(P(`วาระย่อยที่ ${numbering} ${topic.topicName ?? ''}`, 'left', titleIndent))
        if (topic.topicDescription) acc.push(PFirstLinePdf(topic.topicDescription, 'justify', descIndent))
        renderProjects(topic.projects, numbering, acc, projIndent)
        if (topic.children?.length) renderTopics(topic.children, acc, nextPrefix, depth + 1)
    })
}

const buildDocDefinition = (agenda: ExportAgenda) => {
    const beYear = new Date(agenda.meetingDate).getFullYear() + 543
    const when = fmtThaiDateRange(agenda.meetingDate, agenda.meetingStartTime, agenda.meetingEndTime)
    const content = [
        PBold('ระเบียบวาระการประชุม', 'center', 28),
        P(agenda.agendaName || 'คณะกรรมการพิจารณาจริยธรรมการวิจัยในคน กลุ่มสหสถาบันชุดที่ 1 จุฬาลงกรณ์มหาวิทยาลัย', 'center', 0, 20),
        P(`ครั้งที่ ${agenda.agendaNo} ${when}`, 'center', 0, 20),
        P(agenda.meetingLocation ?? '', 'center', 0, 20),
        P('—'.repeat(22), 'center', 0, 20),
    ]
    renderTopics(agenda.topics, content)
    return {
        pageSize: 'A4',
        pageMargins: [72, 72, 72, 72],
        defaultStyle: { font: 'Sarabun', fontSize: 14, lineHeight: 1.2 },
        content,
    } as any
}

export const buildAgendaPdfBlob = async (agenda: ExportAgenda): Promise<Blob> => {
    ensurePdfMakeThaiFonts()
    const docDefinition = buildDocDefinition(agenda)
    return await new Promise<Blob>((resolve) => {
        const pdf: any = pdfMake.createPdf(docDefinition)
        pdf.getBlob((blob: Blob) => resolve(blob))
    })
}

export const downloadAgendaPdf = async (agenda: ExportAgenda) => {
    ensurePdfMakeThaiFonts()
    const docDefinition = buildDocDefinition(agenda)
    const dateStr = formatYmd(agenda.meetingDate || new Date())
    const fileName = `ระเบียบวาระการประชุม_${dateStr}.pdf`
    pdfMake.createPdf(docDefinition).download(fileName)
}
